import React, { useCallback, useContext, useMemo, useState, useEffect } from 'react';
import { Grid, Tooltip } from '@material-ui/core';
import { get } from 'lodash';
import { css } from '@emotion/react';
import AccountHeader from './AccountHeader';
import { setImpersonatedClientId } from '@/utilities/impersonation';
import { useClientUser } from '@/hooks';
import * as ROUTES from '@/constants/routes';
import { UserContext } from '../App';
import { useNavigate } from 'react-router';
import CorpAdminStore, { CorpAdminContext } from './store';
import AddCarriersDrawer from './modals/AddCarriersDrawer';
import CreateClientModal from './modals/CreateClientModal';
import AccountDetailsModal from './modals/AccountDetailsModal';
import { CREATE_SHIPPER_CONFIG } from './modals/createClientConfigs';
import Snackbar from '../Snackbar';
import {
    Button as DesignButton,
    MultiCombobox,
    SearchInput,
    Tabs,
    TabsList,
    TabsTrigger,
} from '@onward-delivery/design';
import { Icon, List, ListChecks } from '@onward-delivery/design/icons';
import CarriersTab from './Carriers/CarriersTab';
import ShippersTable from './shippers/ShippersTable';
import OnboardingView from './Onboarding';
import { TABS_LIST_CLASS, TABS_TRIGGER_CLASS } from './constants';
import GuideButton from './Onboarding/components/GuideButton';
import { useShipperTypeUpdate } from './shippers/hooks';
import { shipperOptionsFor } from './utils';

const CREATE_SHIPPER_ACTION = { label: 'Add Shippers', config: CREATE_SHIPPER_CONFIG };

const SEARCHABLE = ['email', 'business_name', 'client_id'];

const TABS = [
    { header: 'Shippers', kind: 'shipper', action: CREATE_SHIPPER_ACTION },
    { header: 'Carriers', kind: 'carrier' },
];

// List view is the account tables; Onboarding view is where each account stands in onboarding.
const VIEWS = [
    { value: 'list', label: 'List View', icon: List },
    { value: 'onboarding', label: 'Onboarding View', icon: ListChecks },
];

const NO_SHIPPER_FILTER = [];

const NO_ORGANIZATION_TOOLTIP = 'Your account is not associated with an organization — contact support.';

const CorporateUsersInner = () => {
    const { admin_clients, setClientId } = useContext(UserContext);
    const { organization_id, organization, businessName, isOrgImposter } = useClientUser();
    const { state, callbacks } = useContext(CorpAdminContext);
    const { notification, assignmentsByShipper, assignmentsUnavailable, assignmentsError } = state;
    const { setNotification } = callbacks;
    const navigate = useNavigate();

    const [tabIndex, setTabIndex] = useState(0);
    const tab = TABS[tabIndex];
    const [view, setView] = useState('list');
    const [search, setSearch] = useState('');
    const [statusFilters, setStatusFilters] = useState({});
    const [selectedShipperIds, setSelectedShipperIds] = useState([]);
    const [createConfig, setCreateConfig] = useState(null);
    const [addCarriersOpen, setAddCarriersOpen] = useState(false);
    // One modal for the page. The target outlives the close so the tabs don't swap mid-transition.
    const [detailsTarget, setDetailsTarget] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);

    const { updateShipperType, isUpdating: isShipperTypeUpdating } = useShipperTypeUpdate({
        onSuccess: () => setNotification({ severity: 'success', message: 'Routing updated' }),
        onError: () => setNotification({ severity: 'error', message: 'Failed to update routing' }),
    });

    const openDetails = useCallback((target) => {
        setDetailsTarget(target);
        setDetailsOpen(true);
    }, []);

    useEffect(() => {
        // Each tab has its own filter set; drop them on switch.
        setStatusFilters({});
    }, [tabIndex]);

    const setFilter = (key, level) => setStatusFilters((prev) => ({ ...prev, [key]: level }));

    const selectTabByHeader = (header) => {
        const next = TABS.findIndex((t) => t.header === header);
        if (next >= 0) setTabIndex(next);
    };

    const isCarrierTab = tab?.kind === 'carrier';
    // Add Shippers / Add Carriers go through onward-functions, which takes the org from the signed-in
    // login. During an org sim that's the Onward admin's own, so those would act on the wrong org.
    const actionsDisabled = !organization_id || isOrgImposter;
    const actionTooltip = !organization_id
        ? NO_ORGANIZATION_TOOLTIP
        : isOrgImposter
        ? 'Not available while simulating an org admin.'
        : '';
    // Onboarding View searches name, email and account ID only (Onboarding/utils.js filterRows).
    const searchPlaceholder =
        isCarrierTab && view === 'list'
            ? 'Search name, email, DOT #, account ID, or airport'
            : 'Search name, email, or account ID';

    const shipperClients = useMemo(() => {
        if (tab?.kind !== 'shipper') return [];
        return (admin_clients || [])
            .filter((client) => {
                if (!client?.user?.roles?.['SHIPPER']) return false;
                if (statusFilters.shipper_type) {
                    const val = client?.shipper_type || 'MARKETPLACE';
                    if (val !== statusFilters.shipper_type) return false;
                }
                if (search) {
                    const q = search.toLowerCase();
                    return SEARCHABLE.some((field) => {
                        const value = get(client, field);
                        return value && value.toString().toLowerCase().includes(q);
                    });
                }
                return true;
            })
            .sort((a, b) => (a?.business_name || '').localeCompare(b?.business_name || ''));
    }, [admin_clients, search, tab, statusFilters]);

    const shipperOptions = useMemo(() => shipperOptionsFor(admin_clients), [admin_clients]);

    // Until the assignments arrive, a carrier with no map link may still serve the shipper.
    const shipperFilterIds = assignmentsUnavailable ? NO_SHIPPER_FILTER : selectedShipperIds;
    const shipperFilter =
        isCarrierTab && view === 'list' ? (
            <MultiCombobox
                className="w-64 shrink-0"
                options={shipperOptions}
                value={selectedShipperIds}
                onValueChange={setSelectedShipperIds}
                placeholder="All shippers"
                searchPlaceholder="Search shippers…"
                emptyText="No shippers found"
                disabled={assignmentsUnavailable}
                // Shares its row with search and the action buttons.
                maxDisplay={1}
            />
        ) : null;

    const signInAsUser = useCallback(
        (client) => {
            setImpersonatedClientId(client.client_id);
            setClientId(client.client_id);
        },
        [setClientId]
    );

    const signInAsCarrier = useCallback(
        (row) => {
            signInAsUser(row);
            navigate(ROUTES.ACCOUNT);
        },
        [signInAsUser, navigate]
    );

    return (
        <Grid
            container
            css={css`
                flex-direction: column;
                flex-wrap: nowrap;
                height: 100%;
            `}
        >
            <Snackbar
                open={!!notification.message}
                {...notification}
                handleClose={() => setNotification({})}
                autohide={4000}
            />
            <CreateClientModal
                open={!!createConfig}
                onClose={() => setCreateConfig(null)}
                config={createConfig || CREATE_SHIPPER_CONFIG}
            />
            <AddCarriersDrawer open={addCarriersOpen} onClose={() => setAddCarriersOpen(false)} />
            <AccountDetailsModal
                open={detailsOpen}
                context={detailsTarget?.context}
                clientId={detailsTarget?.clientId}
                clientName={detailsTarget?.clientName}
                onClose={() => setDetailsOpen(false)}
            />

            <Grid
                item
                css={css`
                    padding: 2rem 2.5rem 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                `}
            >
                <div
                    css={css`
                        flex-grow: 1;
                    `}
                >
                    <AccountHeader name={organization?.name || businessName || 'Accounts'} />
                </div>
                {view === 'onboarding' ? <GuideButton kind={tab?.kind} /> : null}
            </Grid>

            <Grid
                item
                css={css`
                    padding: 0 2.5rem 1rem;
                `}
            >
                <div className="onward-design">
                    <div className="flex flex-wrap items-center gap-3">
                        <Tabs value={tab?.header || ''} onValueChange={(header) => selectTabByHeader(header)}>
                            <TabsList className={TABS_LIST_CLASS}>
                                {TABS.map((t) => (
                                    <TabsTrigger key={t.header} value={t.header} className={TABS_TRIGGER_CLASS}>
                                        {t.header}
                                    </TabsTrigger>
                                ))}
                            </TabsList>
                        </Tabs>

                        <Tabs value={view} onValueChange={setView}>
                            <TabsList className={TABS_LIST_CLASS}>
                                {VIEWS.map((v) => (
                                    <TabsTrigger key={v.value} value={v.value} className={TABS_TRIGGER_CLASS}>
                                        <Icon icon={v.icon} size="sm" className="mr-1.5 size-3.5" />
                                        {v.label}
                                    </TabsTrigger>
                                ))}
                            </TabsList>
                        </Tabs>

                        <SearchInput
                            className="min-w-48 flex-1"
                            placeholder={searchPlaceholder}
                            onValueChange={setSearch}
                        />

                        {shipperFilter}

                        <div className="flex items-center gap-3.5">
                            {isCarrierTab ? (
                                <Tooltip title={actionTooltip}>
                                    {/* span wrapper so Tooltip can attach to a disabled button */}
                                    <span>
                                        <DesignButton
                                            disabled={actionsDisabled}
                                            onClick={() => setAddCarriersOpen(true)}
                                        >
                                            Add Carriers
                                        </DesignButton>
                                    </span>
                                </Tooltip>
                            ) : null}
                            {tab?.action ? (
                                <Tooltip title={actionTooltip}>
                                    {/* span wrapper so Tooltip can attach to a disabled button */}
                                    <span>
                                        <DesignButton
                                            disabled={actionsDisabled}
                                            onClick={() => setCreateConfig(tab.action.config)}
                                        >
                                            {tab.action.label}
                                        </DesignButton>
                                    </span>
                                </Tooltip>
                            ) : null}
                        </div>
                    </div>
                </div>
            </Grid>

            <Grid
                item
                css={css`
                    padding: 0 2.5rem 2.5rem;
                    display: flex;
                    flex-grow: 1;
                    overflow-y: hidden;
                `}
            >
                {view === 'onboarding' ? (
                    <OnboardingView kind={tab?.kind} search={search} />
                ) : isCarrierTab ? (
                    <CarriersTab
                        search={search}
                        shipperIds={shipperFilterIds}
                        onOpenDetails={openDetails}
                        onSignIn={signInAsCarrier}
                    />
                ) : (
                    <ShippersTable
                        clients={shipperClients}
                        assignmentsByShipper={assignmentsByShipper}
                        assignmentsUnavailable={assignmentsUnavailable}
                        assignmentsError={assignmentsError}
                        statusFilters={statusFilters}
                        setFilter={setFilter}
                        isRoutingUpdating={isShipperTypeUpdating}
                        onRoutingChange={updateShipperType}
                        onSignIn={signInAsUser}
                        onOpen={(client) => navigate(ROUTES.corporateShipperPath(client.client_id))}
                        onDetails={(client) =>
                            openDetails({
                                context: 'SHIPPER',
                                clientId: client.client_id,
                                clientName: client.business_name,
                            })
                        }
                    />
                )}
            </Grid>
        </Grid>
    );
};

const CorporateUsers = () => (
    <CorpAdminStore>
        <CorporateUsersInner />
    </CorpAdminStore>
);

export default CorporateUsers;
